import { useNavigate } from 'react-router-dom';
import { Minus, Plus, Trash2, ShoppingBag, ArrowLeft } from 'lucide-react';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import Sidebar from './Sidebar';

const Cart = () => {
  const { cartItems, removeFromCart, updateQuantity, total } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  const handleCheckout = () => {
    toast.error('Online ordering is temporarily disabled due to relocation. Please order via phone: 07521 262119');
  };

  if (cartItems.length === 0) {
    return (
      <>
        <Sidebar />
        <section className="min-h-screen flex items-center justify-center bg-white dark:bg-gray-800 py-12 sm:py-28">
          <div className="text-center max-w-md mx-auto p-4 sm:p-6">
            <div className="mb-4 sm:mb-6">
              <ShoppingBag className="w-12 h-12 sm:w-16 sm:h-16 text-emerald-600 dark:text-emerald-400 mx-auto" />
            </div>
            <h2 className="font-cormorant text-xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-3 sm:mb-4">
              Your Cart is Empty
            </h2>
            <p className="font-inter text-gray-600 dark:text-gray-300 mb-6 sm:mb-8 text-xs sm:text-base">
              Looks like you haven't added any items to your cart yet.
            </p>
            <button
              onClick={() => navigate('/menu')}
              className="inline-flex items-center justify-center space-x-2 bg-emerald-600 text-white font-inter text-xs sm:text-base font-medium py-2 sm:py-3 px-3 sm:px-6 rounded-full hover:bg-emerald-700 transition-colors duration-300"
            >
              <ArrowLeft className="w-3 h-3 sm:w-4 sm:h-4" />
              <span>Continue Shopping</span>
            </button>
          </div>
        </section>
      </>
    );
  }

  return (
    <>
      <Sidebar />
      <section className="min-h-screen bg-white dark:bg-gray-800 py-12 sm:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-6 sm:mb-12">
            <h2 className="font-cormorant text-xl sm:text-3xl lg:text-5xl font-bold text-gray-900 dark:text-white">
              Shopping Cart
            </h2>
            <button
              onClick={() => navigate('/menu')}
              className="inline-flex items-center font-inter text-gray-600 dark:text-gray-300 hover:text-emerald-600 dark:hover:text-emerald-400 text-xs sm:text-base"
            >
              <ArrowLeft className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
              Continue Shopping
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
            {/* Cart Items */}
            <div className="lg:col-span-2">
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden">
                {/* Horizontal scroll on mobile, vertical list on desktop */}
                <div className="flex flex-row overflow-x-auto sm:flex-col snap-x snap-mandatory pb-2 sm:pb-0">
                  {cartItems.map((item) => (
                    <div
                      key={item.id}
                      className="flex-shrink-0 w-64 sm:w-full snap-start flex flex-col p-3 sm:p-6 border-r sm:border-r-0 border-b-0 sm:border-b border-gray-200 dark:border-gray-700 last:border-r-0 sm:last:border-b-0"
                    >
                      <img
                        src={item.image}
                        alt={item.name}
                        className="w-full h-32 sm:w-24 sm:h-24 object-cover rounded-lg mb-2 sm:mb-0 mx-auto sm:mx-0"
                      />
                      <div className="flex-1 text-center sm:text-left">
                        <h3 className="font-cormorant text-sm sm:text-xl font-semibold text-gray-900 dark:text-white truncate">
                          {item.name}
                        </h3>
                        <p className="font-inter text-gray-600 dark:text-gray-300 text-xs sm:text-sm mt-1 line-clamp-2">
                          {item.description}
                        </p>
                        <div className="mt-2 flex flex-col sm:flex-row items-center justify-between gap-2 sm:gap-4">
                          <p className="font-inter text-sm sm:text-lg font-medium text-emerald-600 dark:text-emerald-400">
                            £{item.price.toFixed(2)}
                          </p>
                          <div className="flex items-center space-x-2 sm:space-x-4">
                            <div className="flex items-center space-x-1 sm:space-x-2 bg-gray-100 dark:bg-gray-700 rounded-full">
                              <button
                                onClick={() => updateQuantity(item.id, Math.max(0, item.quantity - 1))}
                                className="p-1 sm:p-2 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-full"
                              >
                                <Minus className="w-3 h-3 sm:w-4 sm:h-4 text-gray-600 dark:text-gray-300" />
                              </button>
                              <span className="font-inter text-gray-900 dark:text-white w-6 sm:w-8 text-center text-xs sm:text-base">
                                {item.quantity}
                              </span>
                              <button
                                onClick={() => updateQuantity(item.id, item.quantity + 1)}
                                className="p-1 sm:p-2 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-full"
                              >
                                <Plus className="w-3 h-3 sm:w-4 sm:h-4 text-gray-600 dark:text-gray-300" />
                              </button>
                            </div>
                            <button
                              onClick={() => removeFromCart(item.id)}
                              className="p-1 sm:p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-full"
                              aria-label="Remove item"
                            >
                              <Trash2 className="w-4 h-4 sm:w-5 sm:h-5" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-4 sm:p-6 sticky top-24">
                <h3 className="font-cormorant text-base sm:text-xl font-semibold text-gray-900 dark:text-white mb-3 sm:mb-4">
                  Order Summary
                </h3>
                <div className="space-y-3 sm:space-y-4">
                  <div className="flex justify-between font-inter text-gray-600 dark:text-gray-300 text-xs sm:text-base">
                    <span>Subtotal</span>
                    <span>£{total.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between font-inter text-gray-600 dark:text-gray-300 text-xs sm:text-base">
                    <span>VAT (20%)</span>
                    <span>£{(total * 0.2).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between font-inter text-gray-600 dark:text-gray-300 text-xs sm:text-base">
                    <span>Delivery</span>
                    <span>Free</span>
                  </div>
                  <div className="border-t border-gray-200 dark:border-gray-700 pt-3 sm:pt-4 mt-3 sm:mt-4">
                    <div className="flex justify-between font-cormorant text-base sm:text-xl font-semibold text-gray-900 dark:text-white">
                      <span>Total</span>
                      <span>£{(total * 1.2).toFixed(2)}</span>
                    </div>
                    <p className="font-inter text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-1">
                      Including VAT
                    </p>
                  </div>
                  <div className="bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/30 p-4 rounded-xl mt-6">
                    <p className="text-emerald-800 dark:text-emerald-400 text-sm font-medium text-center">
                      Online ordering is temporarily disabled due to relocation.
                      <br /> We are still taking <span className="font-bold">Party</span> & <span className="font-bold">Takeaway Orders</span> via phone.
                      <br /> Please call <a href="tel:07521262119" className="font-bold underline">07521 262119</a>.
                    </p>
                  </div>
                  <button
                    disabled
                    className="w-full bg-gray-400 text-white font-inter text-xs sm:text-base font-medium py-2 sm:py-3 px-3 sm:px-6 rounded-full cursor-not-allowed mt-4"
                  >
                    Checkout Disabled
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Cart;